import { useMutation } from "@tanstack/react-query";
import ReactECharts from "echarts-for-react";
import { useState } from "react";
import { api } from "../api/client.js";
import { ErrorBanner, Panel, pct } from "../components/Common.js";

interface CorrelationResult {
  tickers: string[];
  correlation: number[][];
  covarianceAnnualized: number[][];
  wasPsdRepaired: boolean;
  averagePairwiseCorrelation: number;
  pca: { eigenvalues: number[]; varianceExplained: number[] };
  volatilitiesAnnualized: number[];
}

function Heatmap({ tickers, matrix, title }: { tickers: string[]; matrix: number[][]; title: string }) {
  const data: [number, number, number][] = [];
  matrix.forEach((row, i) => row.forEach((v, j) => data.push([j, i, Number(v.toFixed(4))])));
  const option = {
    tooltip: { position: "top" },
    grid: { left: 70, right: 20, top: 20, bottom: 60 },
    xAxis: { type: "category", data: tickers, splitArea: { show: true }, axisLabel: { color: "#94a3b8" } },
    yAxis: { type: "category", data: tickers, splitArea: { show: true }, axisLabel: { color: "#94a3b8" } },
    visualMap: {
      min: -1,
      max: 1,
      calculable: true,
      orient: "horizontal",
      left: "center",
      bottom: 0,
      inRange: { color: ["#3b82f6", "#0b1220", "#f87171"] },
      textStyle: { color: "#94a3b8" },
    },
    series: [
      {
        name: title,
        type: "heatmap",
        data,
        label: { show: true, color: "#e2e8f0", fontSize: 10 },
      },
    ],
  };
  return <ReactECharts option={option} style={{ height: 360 }} theme="dark" />;
}

export function CorrelationPage() {
  const [tickersInput, setTickersInput] = useState("AAPL,MSFT,NVDA,AMZN");
  const [startDate, setStartDate] = useState("2025-01-01");
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [estimator, setEstimator] = useState("ledoit-wolf");

  const mutation = useMutation({
    mutationFn: () =>
      api.post<CorrelationResult>("/correlation", {
        tickers: tickersInput.split(",").map((t) => t.trim().toUpperCase()).filter(Boolean),
        startDate,
        endDate,
        frequency: "daily",
        estimator,
      }),
  });

  return (
    <div className="space-y-3">
      <Panel title="Basket & methodology">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
          <div className="md:col-span-2">
            <label className="field-label">Tickers (comma-separated)</label>
            <input className="field-input" value={tickersInput} onChange={(e) => setTickersInput(e.target.value)} />
          </div>
          <div>
            <label className="field-label">Start date</label>
            <input type="date" className="field-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div>
            <label className="field-label">End date</label>
            <input type="date" className="field-input" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          <div>
            <label className="field-label">Estimator</label>
            <select className="field-input" value={estimator} onChange={(e) => setEstimator(e.target.value)}>
              <option value="pearson">Pearson</option>
              <option value="spearman">Spearman rank</option>
              <option value="ewma">EWMA</option>
              <option value="ledoit-wolf">Ledoit-Wolf shrinkage</option>
            </select>
          </div>
        </div>
        <button className="btn-primary mt-3" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
          {mutation.isPending ? "Computing…" : "Run analysis"}
        </button>
      </Panel>

      {mutation.error && <ErrorBanner message={(mutation.error as Error).message} />}

      {mutation.data && (
        <>
          {mutation.data.wasPsdRepaired && (
            <div className="rounded-md border border-amber-800/50 bg-amber-950/30 px-3 py-2 text-xs text-amber-300">
              The estimated correlation matrix was not positive semidefinite and was repaired via the
              Higham nearest-correlation-matrix method before use.
            </div>
          )}
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <Panel title="Correlation matrix">
              <Heatmap tickers={mutation.data.tickers} matrix={mutation.data.correlation} title="Correlation" />
            </Panel>
            <Panel title="Annualized covariance matrix">
              <Heatmap
                tickers={mutation.data.tickers}
                matrix={mutation.data.covarianceAnnualized}
                title="Covariance"
              />
            </Panel>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <Panel title="Average pairwise correlation">
              <div className="kpi-value">{pct(mutation.data.averagePairwiseCorrelation)}</div>
            </Panel>
            <Panel title="Annualized volatilities">
              <ul className="space-y-1 font-mono text-sm">
                {mutation.data.tickers.map((t, i) => (
                  <li key={t}>
                    {t}: {pct(mutation.data.volatilitiesAnnualized[i]!)}
                  </li>
                ))}
              </ul>
            </Panel>
            <Panel title="PCA variance explained">
              <ul className="space-y-1 font-mono text-sm">
                {mutation.data.pca.varianceExplained.map((v, i) => (
                  <li key={i}>
                    PC{i + 1}: {pct(v)}
                  </li>
                ))}
              </ul>
            </Panel>
          </div>
        </>
      )}
    </div>
  );
}
